import { HttpParams } from '@angular/common/http';
import { AfterViewInit, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { ChatbotService } from '@ecm/consumer';
import { EnvUtils } from '@ecm/dispatcher';
import { EcmMaterialModule } from '@ecm/material';
import {
    CarrouselItem,
    ContextService,
    Contract,
    Portfolio,
    PortfolioFlagsService,
    PortfolioService,
} from '@ecm/portfolio';
import { TitleService } from '@ecm/router';

import { ClientTypeEnum } from '../../business/api/dashboard/forbidden/clientType.enum';
import { DashboardComponent } from '../../components/dashboard/dashboard-component/dashboard.component';
import { ForbiddenComponent } from '../../features/forbidden/container/forbidden/forbidden.component';
import { DashboardContainerComponentHelpers } from '../../helper/dashboardContainerComponentHelpers';

@Component({
    selector: 'dashboard-container',
    templateUrl: 'dashboard-container.component.html',
    standalone: true,
    imports: [DashboardComponent, ForbiddenComponent, EcmMaterialModule],
})
export class DashboardContainerComponent implements OnInit, OnDestroy, AfterViewInit {
    public contract: Contract;
    public forbiddenType: ClientTypeEnum;

    public httpParams: HttpParams;

    public canCallApiDashboard = false;
    public isSharedServices = false;
    public canDisplayPage: boolean;
    public sharedServicesNsru = false;
    public sharedServicesItem: CarrouselItem;

    private readonly route = inject(ActivatedRoute);
    private readonly portfolioService = inject(PortfolioService);
    private readonly contextService = inject(ContextService);
    private readonly portfolioFlagsService = inject(PortfolioFlagsService);
    private readonly translate = inject(TranslateService);
    private readonly titleService = inject(TitleService);
    private readonly chatbotService = inject(ChatbotService);

    public isSosh = this.portfolioFlagsService.isSosh;

    private contextId = null;
    private readonly subscription = new Subject();
    private observer!: MutationObserver;

    public ngAfterViewInit() {
        this.route.queryParams.subscribe((params) => {
            this.waitForBotElement('#botmanapp_addClassheader', () => {
                this.openChatBotWithIntent(params);
            });
        });
    }

    public ngOnInit(): void {
        const dashboardTranslation = this.translate.instant('dashboard');
        this.titleService.setTitle(dashboardTranslation['title']);
        this.contextService.contextId$.pipe(takeUntil(this.subscription)).subscribe((contexId) => {
            this.contextId = contexId;
            this.getContent();
        });
    }

    public ngOnDestroy(): void {
        if (this.observer) {
            this.observer.disconnect();
        }
        this.subscription.next(null);
        this.subscription.complete();
    }

    private getContent(): void {
        this.portfolioService
            .getPortfolio()
            .pipe(takeUntil(this.subscription))
            .subscribe((portfolio: Portfolio) => {
                const matchingContract = portfolio.contracts.find(
                    (contract: Contract) => contract.cid === this.contextId,
                );
                const sharedServices = portfolio.carrousel.find(
                    (carroouselItem) => carroouselItem.type === this.contextId,
                );

                if (matchingContract !== undefined) {
                    if (DashboardContainerComponentHelpers.isForbiddenContract(matchingContract)) {
                        this.canCallApiDashboard = false;
                        this.contract = matchingContract;
                        this.forbiddenType = DashboardContainerComponentHelpers.getForbiddenType(matchingContract);
                    } else {
                        this.canCallApiDashboard = true;
                        this.httpParams = this.getHttpParams('contract', this.contextId);
                    }
                } else {
                    this.canCallApiDashboard = false;
                    this.forbiddenType = ClientTypeEnum.NSRU;
                    if (sharedServices) {
                        this.sharedServicesNsru = true;
                        this.sharedServicesItem = sharedServices;
                        this.canCallApiDashboard = true;
                        this.httpParams = this.getHttpParams('sharedServices', null);
                        this.forbiddenType = ClientTypeEnum.SHAREDSERVICES;
                    }
                }

                this.canDisplayPage = true;
            });
    }

    private getHttpParams(type: string, cid?: string): HttpParams {
        let queryParams: HttpParams = new HttpParams();
        const isEclair = EnvUtils.isEclair();

        queryParams = queryParams.set('item_type', type).set('item_value', cid);

        if (!isEclair) {
            queryParams = queryParams.set('display', 'mlb');
        }

        return queryParams;
    }

    private openChatBotWithIntent(params: Params) {
        if (!params?.carrousel || !params?.intent) return;
        const { carrousel, intent } = params;
        let decodedCid: string;
        try {
            decodedCid = atob(carrousel);
        } catch (error) {
            return;
        }
        if (!ContextService.CIDRegexp.test(decodedCid)) return;
        const formattedIntent = `/${intent}`;
        this.chatbotService.openChatbot(formattedIntent, { cid: decodedCid });
    }

    private waitForBotElement(selector: string, callback: (element: HTMLElement) => void) {
        this.observer = new MutationObserver((_mutations, observer) => {
            const element = document.querySelector(selector) as HTMLElement;
            if (element) {
                observer.disconnect();
                setTimeout(() => {
                    callback(element);
                }, 1000);
            }
        });
        this.observer.observe(document.body, { childList: true, subtree: true });
    }
}
